import api from './api';

/**
 * Service pour la gestion admin des réservations
 * Interface administrateur avec endpoints spécialisés
 */
class AdminReservationService {
  
  /**
   * Récupère le nombre de réservations pour une période donnée
   * @param {Object} params - Paramètres de filtre
   * @param {string} params.startDate - Date de début (optionnel, défaut: 30 jours en arrière)
   * @param {string} params.endDate - Date de fin (optionnel, défaut: aujourd'hui)
   * @returns {Promise<Object>} Statistiques avec nombre total de réservations
   */
  async getReservationsCount(params = {}) {
    try {
      const response = await api.get('/admin/reservations/stats/count', { 
        params: {
          startDate: params.startDate,
          endDate: params.endDate
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching reservations count stats:', error);
      throw error;
    }
  }

  /**
   * Récupère le dashboard admin des réservations
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<Object>} Données du dashboard
   */
  async getDashboard(filters = {}) {
    try {
      const response = await api.get('/admin/reservations/dashboard', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin dashboard:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste des réservations admin
   * @param {Object} options - Options de pagination et filtres
   * @returns {Promise<Object>} Liste paginée des réservations
   */
  async getReservations(options = {}) {
    try {
      const response = await api.get('/admin/reservations', { params: options });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin reservations:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques en temps réel
   * @returns {Promise<Object>} Statistiques temps réel
   */
  async getRealtimeStats() {
    try {
      const response = await api.get('/admin/reservations/stats/realtime');
      return response.data;
    } catch (error) {
      console.error('Error fetching realtime stats:', error);
      throw error;
    }
  }

  /**
   * Récupère les alertes sur les réservations
   * @param {Object} options - Options pour les alertes
   * @returns {Promise<Object>} Liste des alertes
   */
  async getAlerts(options = {}) {
    try {
      const response = await api.get('/admin/reservations/alerts', { params: options });
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation alerts:', error);
      throw error;
    }
  }

  /**
   * Marque une alerte comme traitée
   * @param {string} alertId - ID de l'alerte
   * @param {Object} data - Données additionnelles (note, etc.)
   * @returns {Promise<Object>} Résultat du traitement
   */
  async acknowledgeAlert(alertId, data = {}) {
    try {
      const response = await api.post(`/admin/reservations/alerts/${alertId}/acknowledge`, data);
      return response.data;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      throw error;
    }
  }

  /**
   * Liste complète des réservations avec filtres avancés et pagination
   * @param {Object} options - Page, limit, status, search, dates, etc.
   * @returns {Promise<Object>} { reservations, pagination, summary }
   */
  async getReservationsList(options = {}) {
    try {
      const params = {
        page: options.page || 1,
        limit: options.limit || 25,
        sort_by: options.sortBy || 'created_at',
        sort_order: options.sortOrder || 'DESC',
        status: options.status || undefined,
        search: options.search || undefined,
        start_date: options.startDate || undefined,
        end_date: options.endDate || undefined,
        payment_status: options.paymentStatus || undefined,
        has_issues: options.hasIssues || undefined,
        user_id: options.userId || undefined,
        listing_id: options.listingId || undefined,
      };
      // Remove undefined keys to keep URL clean
      Object.keys(params).forEach(k => params[k] === undefined && delete params[k]);
      const response = await api.get('/admin/reservations', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching reservations list:', error);
      throw error;
    }
  }

  /**
   * Détail complet d'une réservation (enrichi admin)
   * @param {string} id - ID de la réservation
   * @returns {Promise<Object>} Réservation enrichie avec admin_insights
   */
  async getReservationDetail(id) {
    try {
      const response = await api.get(`/admin/reservations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation detail:', error);
      throw error;
    }
  }

  /**
   * Exécuter une action administrative sur une réservation
   * @param {string} id - ID de la réservation
   * @param {string} action - 'force_cancel' | 'approve_payment' | 'refund_deposit' | 'update_status' | 'add_note'
   * @param {Object} data - Données de l'action
   * @returns {Promise<Object>} Résultat
   */
  async performAction(id, action, data = {}) {
    try {
      const response = await api.post(`/admin/reservations/${id}/actions`, { action, data });
      return response.data;
    } catch (error) {
      console.error('Error performing reservation action:', error);
      throw error;
    }
  }
}

export default new AdminReservationService();